import { Inject, Injectable, Injector } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { catchError, finalize, from, Observable, shareReplay, switchMap, throwError as observableThrowError, of, tap, throwError } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { NgxSpinnerService } from 'ngx-spinner';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { OAuthStorage } from 'angular-oauth2-oidc';
import { Globals } from 'src/app/globals/globals';
import { AuthenticationService } from '../services/auth/auth.service';
import { ToastrService } from 'ngx-toastr';
import { MessageService } from 'primeng/api';
import { TokenstorageService } from '../services/token-storage/tokenstorage.service';
import { WorkspaceService } from '../services/workspace/workspace.service';
import { SKIP_AUTH_RECOVERY } from './auth-recovery.context';

@Injectable()
export class InterceptorService implements HttpInterceptor {
	public lastResponseCode: number = 0;
	constructor(private router: Router, public globals: Globals,
		private authStorage: OAuthStorage,
		private authService: AuthenticationService,
		private tokenStorageService: TokenstorageService,
		@Inject(Injector) private injector: Injector) { }
	private getTimezoneOffset(): string {
		return (String(new Date().getTimezoneOffset() * -1));
	}

	private get toastrService(): ToastrService {
		return this.injector.get(ToastrService);
	}

	// Local dedupe for concurrent 401s; the authoritative global single-flight
	// is AuthenticationService's refreshToken patch. Kept so the interceptor is
	// self-contained under test.
	private tokenRefresh$: Observable<any> | null = null;
	// Once per page-life: re-login navigates away, and N concurrent failures
	// must not race N toasts + N redirects.
	private reloginInitiated = false;

	private refreshAccessToken(): Observable<any> {
		if (!this.tokenRefresh$) {
			const oauth = this.injector.get(OAuthService);
			this.tokenRefresh$ = from(oauth.refreshToken()).pipe(
				finalize(() => { this.tokenRefresh$ = null; }),
				shareReplay(1)
			);
		}
		return this.tokenRefresh$;
	}

	private forceRelogin(message: string, err: any): Observable<never> {
		if (!this.reloginInitiated) {
			this.reloginInitiated = true;
			this.toastrService.warning(message);
			this.authService.logout();
			this.authService.configureOAuthAndLogin();
		}
		return throwError(() => err);
	}

	/**
	 * Credential recovery for 353 (workspace_key) and API 401 (bearer): refresh
	 * the failed credential once, replay, and let the two CHAIN — a long-idle
	 * tab expires both, so the replay after a token refresh may 353 and vice
	 * versa. Each credential refreshes at most once per original request, so
	 * recursion is bounded. Refresh failures force re-login (once); any other
	 * replay error propagates untouched. The refresh-failure catchError sits
	 * BEFORE switchMap so it can never swallow a replay error.
	 */
	private recoverAuthError(response: any, req: HttpRequest<any>, next: HttpHandler,
		triedToken: boolean, triedWsKey: boolean): Observable<HttpEvent<any>> {
		if (!(response instanceof HttpErrorResponse)) {
			return throwError(() => response);
		}

		if (response.status === 353 && !triedWsKey) {
			const workspaceService = this.injector.get(WorkspaceService);
			return workspaceService.refreshWorkspaceKey().pipe(
				catchError((refreshErr: any) =>
					this.forceRelogin('Could not refresh session. Please log in again.', refreshErr)),
				switchMap((res: any) => {
					if (!(res && res.flag && res.data && res.data.workspace_key)) {
						return this.forceRelogin('Your session has expired. Please log in again.', response);
					}
					this.globals.workspace_key = res.data.workspace_key;
					if (res.data.actions) {
						this.globals.workspaceActions = res.data.actions;
					}
					const retryReq = req.clone({
						setHeaders: { workspace_key: this.globals.workspace_key }
					});
					return next.handle(retryReq).pipe(
						catchError((e: any) => this.recoverAuthError(e, retryReq, next, triedToken, true)));
				})
			);
		}

		if (response.status === 401 && req.url.startsWith(environment.serviceurl)) {
			if (triedToken) {
				// Fresh token, still 401 — a genuine denial. Deduped like every
				// other toast here (lastResponseCode resets on the next success).
				if (this.lastResponseCode !== 401) {
					this.toastrService.warning('You are not authorized to perform this action.');
				}
				this.lastResponseCode = 401;
				return throwError(() => response);
			}

			// Replay-first: if ANOTHER TAB has already refreshed (storage holds a
			// newer token than the one this request carried), use it instead of
			// refreshing again — a second refresh would revoke that tab's new
			// token (the IdentityServer retires the previous pair on every
			// exchange) and start a cross-tab ping-pong that ends in re-login.
			const carried = (req.headers.get('Authorization') || '').replace(/^Bearer\s+/i, '');
			const inStorage = this.authStorage.getItem('access_token');
			if (inStorage && carried && inStorage !== carried) {
				const replayReq = req.clone({ setHeaders: { Authorization: 'Bearer ' + inStorage } });
				return next.handle(replayReq).pipe(
					catchError((e: any) => this.recoverAuthError(e, replayReq, next, true, triedWsKey)));
			}

			return this.refreshAccessToken().pipe(
				catchError((refreshErr: any) =>
					this.forceRelogin('Your session has expired. Please log in again.', refreshErr)),
				switchMap(() => {
					const fresh = this.authStorage.getItem('access_token');
					if (!fresh) {
						return this.forceRelogin('Your session has expired. Please log in again.', response);
					}
					const retryReq = req.clone({
						setHeaders: { Authorization: 'Bearer ' + fresh }
					});
					return next.handle(retryReq).pipe(
						catchError((e: any) => this.recoverAuthError(e, retryReq, next, true, triedWsKey)));
				})
			);
		}

		return throwError(() => response);
	}


	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

		let par: any = request.params;
		const reqheaders: any = {
			"X-Timezone-Offset": this.getTimezoneOffset()
		};

		// Handle multipart data
		if (request.params.has("multiPartData")) {
			par = request.params.delete("multiPartData");
			reqheaders["enctype"] = "multipart/form-data";
		}

		// "noWorkspaceKey" marker: WorkspaceService.refreshWorkspaceKey sets this
		// so the in-flight refresh call doesn't attach the expired workspace_key
		// header. Without skipping, the refresh would itself bounce off the
		// middleware's TTL check and we'd loop forever on 353.
		let skipWorkspaceKey = false;
		if (request.params.has("noWorkspaceKey")) {
			par = par.delete("noWorkspaceKey");
			skipWorkspaceKey = true;
		}

		// Check if request is to service URL (do this once)
		const isServiceUrl = request.url.startsWith(environment.serviceurl);

		if (isServiceUrl) {
			// Set content type for service URLs (if not multipart). A FormData body is multipart
			// whether or not the caller added the marker: labelled JSON, the server reads no form
			// and every uploaded file arrives empty (ontology imports and pack files did).
			if (!request.params.has("multiPartData") && !(request.body instanceof FormData)) {
				reqheaders["Content-Type"] = "application/json";
			}

			// Add authorization token
			const token = this.authStorage.getItem('access_token');
			if (token) {
				reqheaders["Authorization"] = 'Bearer ' + token;
				this.tokenStorageService.setToken(token);
			}

			// Add workspace headers if available (single if block)
			if (this.globals) {
				if (this.globals.WorkSpaceID?.length > 0) {
					reqheaders["workspaceid"] = this.globals.WorkSpaceID;
				}
				if (this.globals.workspace_key?.length > 0 && !skipWorkspaceKey) {
					reqheaders["workspace_key"] = this.globals.workspace_key;
				}
				if (this.globals.TenantUniqueKey?.length > 0) {
					reqheaders["tenant_unique_key"] = this.globals.TenantUniqueKey;
				}
			}
		}


		request = request.clone({
			withCredentials: true,
			setHeaders: reqheaders,
			params: par
		});

		// Captured AFTER the header-setting clone — deliberately. Recovery
		// replays override ONLY the credential being refreshed, so every other
		// header (bearer/workspace_key/timezone/content-type, incl. multipart)
		// must already be on this reference. Do not move above the clone.
		const originalRequest = request;

		return next.handle(request).pipe(
			catchError(response => {
				if (response instanceof HttpErrorResponse) {
					// Background calls (seat heartbeat and friends) opt out of ALL
					// recovery and toasts: a failed beat is skipped, never a reason to
					// refresh a token or log the user out (see auth-recovery.context.ts).
					if (request.context.get(SKIP_AUTH_RECOVERY)) {
						return throwError(() => response);
					}

					// 353 = workspace_key TTL expired (WorkspaceMiddleware.cs);
					// API 401 = expired bearer, or a genuine denial — decided after
					// one refresh+replay. Shared recovery handles both, including
					// the long-idle case where BOTH expired and the replay of one
					// hits the other.
					if (response.status === 353 || (response.status === 401 && isServiceUrl)) {
						return this.recoverAuthError(response, originalRequest, next, false, false);
					}

					// Handle error responses with status code tracking to prevent duplicate notifications
					if (this.lastResponseCode !== response.status) {
						switch (response.status) {
							case 350:
								// Session expired - logout and re-authenticate
								this.authService.logout();
								this.authService.configureOAuthAndLogin();
								break;
							case 401:
								// Non-API (asset/identity) 401s only — API 401s are
								// handled by the refresh-and-retry branch above.
								this.toastrService.warning("You are unauthorized to view this page");
								break;
							case 500:
								// Server error
								this.toastrService.warning('Something went wrong at server.');
								break;
							case 352:
								// Force full OAuth re-authentication. Reserved for
								// "OAuth session itself must be reset" conditions.
								// Workspace_key TTL expiry uses 353 (silent refresh,
								// see above); 352 is the heavy-hammer path.
								{
									const msg352 = (typeof response.error === 'string' && response.error)
										? response.error
										: 'Your session has expired. Please log in again.';
									this.toastrService.warning(msg352);
								}
								this.authService.logout();
								this.authService.configureOAuthAndLogin();
								break;
							case 429:
								// Rate limit exceeded
								this.toastrService.warning('Too many requests. Please try again later.');
								break;
						}
						this.lastResponseCode = response.status;
					}
				}
				return throwError(() => response);
			}),
			tap((ev: HttpEvent<any>) => {
				if (ev instanceof HttpResponse) {
					this.lastResponseCode = ev.status;
				}
			})
		);
	}
}

@Injectable()
export class LoaderService implements HttpInterceptor {
	counter = 0;
	constructor(private spinner: NgxSpinnerService, private globals: Globals) { }
	intercept(req: HttpRequest<any>, next: HttpHandler) {
		if (!req.params.has("silentServerFetch") && this.globals.spinnerAnimation && !req.url.endsWith("/Identity/connect/token")) {
			this.counter += 1;
			this.spinner.show();

			return next.handle(req).pipe(
				finalize(() => {
					this.counter -= 1;

					if (this.counter < 0)
						this.counter = 0;

					if (this.counter == 0)
						this.spinner.hide();
				})
			);
		} else {
			if (req.params.has("silentServerFetch")) {
				var par = req.params.delete("silentServerFetch");

				req = req.clone({
					withCredentials: true,
					params: par
				});
			}
			return next.handle(req);
		}

	}
}
